import { TooltipContent } from '@design-system';
import { BalanceBreakdownTooltip } from '../BalanceBreakdownTooltip';
import { currency } from '../formatters';
import { HeaderTooltip } from '../HeaderTooltip';
import { indemnityPayoutWaterfall } from './indemnityPayout';
import { reimbursementPayoutRows } from './payoutBreakdown';
import type { StrategyPresentation } from './types';

// Generic two-pool hybrid life insurance — Allstate Whole Life, Securian
// SecureCare III/IV, and Trustmark Life + Care (`agisGroupPolicy`), plus the
// generic `hybridLifeInsurance` type. A death-benefit (base) pool is drawn first
// for LTC, then a Continuation of Benefits (COB) pool; only base drawdown
// accelerates (reduces) the death benefit. The death benefit is held STATIC by
// default (the inflation rider grows the LTC pools, not the life face) — mirrors
// CM II and the other hybrids. Modeling is unverified (no carrier illustration).
// All values are read off precomputed month state/deltas — no strategy math is
// re-implemented here.
export const genericHybridPresentation: StrategyPresentation = {
  // "Pool remaining" — two-pool open → close breakdown (death-benefit base pool
  // drawn first, then the Continuation of Benefits pool). Mirrors the sibling
  // two-pool presentation (oaAssetCarePresentation); only base drawdown also
  // accelerates the death benefit.
  poolCellTooltip: ({ month, openState }) => {
    const openBase = openState.pools.baseBenefitPool;
    const openEob = openState.pools.extensionOfBenefitsPool;
    const closeBase = month.state.pools.baseBenefitPool;
    const closeEob = month.state.pools.extensionOfBenefitsPool;
    const baseDepletion = month.deltas.base.depletion;
    const eobDepletion = month.deltas.eob.depletion;
    const baseAccrual = month.deltas.base.inflationAccrual;
    const eobAccrual = month.deltas.eob.inflationAccrual;

    const allZero =
      openBase === 0 &&
      openEob === 0 &&
      closeBase === 0 &&
      closeEob === 0 &&
      baseDepletion === 0 &&
      eobDepletion === 0 &&
      baseAccrual === 0 &&
      eobAccrual === 0;
    if (allZero) return null;

    return (
      <BalanceBreakdownTooltip
        title="LTC Pool Breakdown"
        description="Pays from the death-benefit pool first, then the Continuation of Benefits (COB) pool. Inflation accrues at policy anniversaries while each pool remains open. Only death-benefit pool drawdown reduces the death benefit; COB drawdown does not."
        sections={[
          {
            heading: 'Death-benefit pool',
            opening: openBase,
            closing: closeBase,
            lines: [
              { label: 'Inflation accrual', value: baseAccrual, kind: 'add' },
              { label: 'Drawn this month', value: baseDepletion, kind: 'subtract' },
            ],
          },
          {
            heading: 'Continuation of Benefits (COB)',
            opening: openEob,
            closing: closeEob,
            lines: [
              { label: 'Inflation accrual', value: eobAccrual, kind: 'add' },
              { label: 'Drawn this month', value: eobDepletion, kind: 'subtract' },
            ],
          },
        ]}
      />
    );
  },

  // "Projected payout" cell — reimbursement (or indemnity) waterfall from care
  // cost to policy paid to out-of-pocket. Built on universal month facts/deltas
  // (eligible cost, policy paid, the calc's per-day out-of-pocket breakdown), so
  // it stays accurate no matter how this product derives its monthly cap (a
  // death-benefit acceleration share vs a daily nominal). Unlike the Asset Care
  // sibling it omits the per-pool days × rate split — that reconciliation
  // assumes a daily-nominal cap this family doesn't necessarily use — and names
  // the funding pool(s) with a plain "drawn from" line instead.
  payoutCellTooltip: ({ month, terms }) => {
    const eligibleDays = month.facts.coverageEligibleDays;
    // Keep the tooltip alive on a fully-ineligible month (waiting period / <2
    // ADL) when either room & board is still out-of-pocket or a retroactive
    // waiting-period lump landed on this row:
    //  - Independent Living books R&B even on non-paying months (cmTwo guards
    //    the same way); it's the exact OOP this modeling exists to surface.
    //  - A retroactive waiting-period lump (SecureCare IV, which reuses this
    //    presentation) is paid on the row that SATISFIES the EP — a row with
    //    zero eligible days; bailing on eligibleDays alone would hide the
    //    breakdown for the one row where real money moved.
    // Both are no-ops for products that emit neither. Otherwise PaidCell shows
    // the zero-row reason natively.
    const epLumpSumPaid =
      (month.deltas.strategyDeltas as { epLumpSumPaid?: number } | undefined)?.epLumpSumPaid ?? 0;
    if (eligibleDays === 0 && (month.facts.roomAndBoardCost ?? 0) === 0 && epLumpSumPaid === 0)
      return null;

    const drawnBase = month.deltas.base.depletion;
    const drawnEob = month.deltas.eob.depletion;


    const mode = terms.isIndemnity ? 'indemnity' : 'reimbursement';
    const modeText = terms.isIndemnity
      ? 'Pays a fixed cash benefit (subject to cap) from the death-benefit pool first, then the COB pool. Cash above the eligible cost banks as a surplus reserve, drained to cover cost on later shortfall months.'
      : 'Reimburses eligible incurred cost, up to the monthly cap. Funded from the death-benefit pool first, then the COB pool.';

    return (
      <TooltipContent className="max-w-xs text-xs">
        <div className="mb-1 font-semibold">Projected payout — {mode}</div>
        <div className="mb-1 text-[10px] text-gray-300">{modeText}</div>
        <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
          {terms.isIndemnity ? (
            // Indemnity is a cash waterfall, not a day-bucket breakdown — see
            // `indemnityPayoutWaterfall`.
            indemnityPayoutWaterfall(month)
          ) : (
            reimbursementPayoutRows({ month })
          )}
        </div>
        {drawnBase > 0 || drawnEob > 0 ? (
          <div className="mt-1 border-t border-gray-700 pt-1 text-[10px] text-gray-400">
            {drawnBase > 0 && drawnEob > 0
              ? `Drawn — death-benefit pool ${currency(drawnBase)}, COB ${currency(drawnEob)}`
              : drawnBase > 0
                ? `Drawn from death-benefit pool ${currency(drawnBase)}`
                : `Drawn from COB ${currency(drawnEob)}`}
          </div>
        ) : null}
      </TooltipContent>
    );
  },

  copy: {
    poolTooltip: (
      <HeaderTooltip
        description="End-of-month LTC pool balance (death-benefit pool + Continuation of Benefits pool), with this month's change beneath. The death-benefit pool is drawn first; the COB pool is drawn only after it is exhausted. Each pool grows on its own inflation rate while open."
        formulas={[
          { label: 'Closing pool', expression: 'opening + inflation accrual − drawn this month' },
          { label: 'Draw order', expression: 'death-benefit pool first, then COB pool' },
        ]}
      />
    ),
    payoutColumnTooltip: (
      <HeaderTooltip
        description="Policy payout this month (reimbursement or indemnity, depending on policy mode), with cumulative dollars paid by the policy through this month in parentheses. Funded from the death-benefit pool first, then the COB pool."
        formulas={[
          {
            label: 'Reimbursement mode',
            expression: 'min(eligible cost, monthly cap, pool remaining)',
          },
          { label: 'Indemnity mode', expression: 'min(monthly cap, pool remaining)' },
        ]}
        notes="Eligible cost excludes waiting-period and <2 ADL days. Indemnity pays the cap as cash regardless of actual cost; cash above the cost builds a surplus drained on later shortfall months."
      />
    ),
    deathBenefitTooltip: (
      <HeaderTooltip
        description="End-of-month death benefit, with this month's change beneath. Held at the at-issue face — it does NOT inflate. LTC draws from the death-benefit pool reduce it dollar-for-dollar; COB draws do not. Whatever remains is paid to beneficiaries."
        formulas={[
          { label: 'Death benefit', expression: 'at-issue face − Σ (death-benefit pool accelerated)' },
          { label: 'Floor', expression: 'max(above, Residual Death Benefit)' },
        ]}
        notes="The death benefit is static by default — the inflation rider grows the LTC pools, not the life face."
      />
    ),
  },
};
